/* eslint-disable max-lines */
import { track } from "jiffy-analytics";
import { on } from "common/utils/ui/event";
import initDesignUploadModal from "components/jiffy/design-upload-cta/design-upload-cta";
import DesignUploadCtaPlpCookieClass from "components/jiffy/design-upload-cta/design-upload-cta-plp/design-upload-cta-plp-cookie-class";
import buildDesignLabUrl from "common/utils/design-lab-url";
import { isMobile, redirectTo } from "common/utils/browser";
import { getActiveColorName, collectPdpData } from "common/jiffy/pdp-data-collection";

const START_DESIGNING_SELECTOR = ".js-start-designing";

// Trigger location constants
const TRIGGER_LOCATIONS = {
  BUSINESS_TILE: "Business Tile",
  TOP_TILE: "Top Tile (G640)",
  PRODUCT_CARD: "Product Card"
};

// DOM getter functions
const $getHeroCard = target => target.closest(".js-hero-card");
const $getProductCard = target => target.closest("[data-catalog-number]");
const $getCustomsNav = target => target.closest("[data-testid='nav-design-lab']");
const $getPdpUploadButton = target => target.closest(".js-product-details-upload-button-wrapper");
const $getBannerCta = target => target.closest("[data-testid='product-details-custom-cta-banner']");
const $getBannerPromo = target => target.closest(".js-header-promo-carousel-banners-grid");
const $getModalDataset = () =>
  document.getElementById("design-upload-modal")?.querySelector(".js-design-upload-cta");

const extractProductData = element => {
  const { catalogNumber, designLibraryUrl, canvaUrl, color, productId, isFeatured, compositionId } =
    element.dataset;
  return {
    catalogNumber,
    designLibraryUrl,
    canvaUrl,
    color,
    productId,
    isFeatured: isFeatured === "true",
    compositionId
  };
};

const parseMixpanelData = mixpanelData => {
  if (!mixpanelData || typeof mixpanelData !== "string") {
    return {};
  }

  try {
    return JSON.parse(mixpanelData);
  } catch (error) {
    console.warn("Failed to parse mixpanel data:", error);
    return {};
  }
};

const handleHeroCard = () => {
  const triggerLocation = TRIGGER_LOCATIONS.BUSINESS_TILE;
  track("upload_step_viewed", { trigger_location: triggerLocation });
};

const extractProductDataWithFallback = (element, productData) => {
  let { catalogNumber, color } = productData || {};

  // Always get the current color from URL first (handles JS color changes without page reload)
  const urlParams = new URLSearchParams(window.location.search);
  const urlCatalogNumber = urlParams.get("catalog_number");
  const urlColor = urlParams.get("ac") || getActiveColorName();

  // For color, always prefer URL over element data (handles dynamic color changes)
  color = urlColor || color;

  if (!catalogNumber) {
    const pdpProductElement = document.querySelector("[data-catalog-number]");
    if (pdpProductElement) {
      catalogNumber = catalogNumber || pdpProductElement.dataset.catalogNumber || urlCatalogNumber;
    } else {
      catalogNumber = catalogNumber || urlCatalogNumber;
    }
  }

  return { catalogNumber, color };
};

const handleRedirect = (element, $link) => {
  const productData = (element && extractProductData(element)) || {};
  const { catalogNumber, color } = extractProductDataWithFallback(element, productData);

  const isAiDesign = $link?.dataset?.aiDesign === "true";
  const params = new URLSearchParams();

  if (isAiDesign) {
    params.set("design-menu", "ai-design");
  }

  if (catalogNumber && color) {
    params.set("catalog_number", catalogNumber);
    params.set("ac", color);
  }

  // Collect PDP data for size and quantity persistence
  const activeColor = color || getActiveColorName();
  if (activeColor) {
    const { quantities, sizeData } = collectPdpData(activeColor);
    if (quantities) {
      params.set("pdp_quantities", JSON.stringify(quantities));
    }
    if (sizeData) {
      params.set("pdp_size_data", JSON.stringify(sizeData));
    }
  }

  const { mixpanelData, mixpanelEvent } = $link?.dataset || {};
  if (!mixpanelEvent) {
    track("start_designing_clicked", {
      ...parseMixpanelData(mixpanelData),
      with_plp_preview: false
    });
  }

  redirectTo(`/design-lab?${params.toString()}`);
};

const buildDesignLabParams = (productData, $link) => {
  const { catalogNumber, color, pdpQuantities, pdpSizeData } = productData;
  const isAiDesign = $link.dataset?.aiDesign === "true";
  const params = new URLSearchParams();

  if (isAiDesign) {
    params.set("design-menu", "ai-design");
  }

  if (catalogNumber && color) {
    params.set("catalog_number", catalogNumber);
    params.set("ac", color);
  }

  if (pdpQuantities) {
    params.set("pdp_quantities", JSON.stringify(pdpQuantities));
  }

  if (pdpSizeData) {
    params.set("pdp_size_data", JSON.stringify(pdpSizeData));
  }

  return params;
};

const collectAndHandleRedirect = (element, $link) => {
  const productData = element ? extractProductData(element) : {};
  const { catalogNumber, color } = extractProductDataWithFallback(element, productData);

  productData.catalogNumber = catalogNumber;
  productData.color = color;

  // Collect PDP data for size and quantity persistence
  const activeColor = color || getActiveColorName();
  if (activeColor) {
    const { quantities, sizeData } = collectPdpData(activeColor);
    if (quantities) {
      productData.pdpQuantities = quantities;
    }
    if (sizeData) {
      productData.pdpSizeData = sizeData;
    }
  }

  // Handle redirect based on element type
  if ($link.tagName === "A") {
    // Update href for anchor tags
    const params = buildDesignLabParams(productData, $link);
    $link.href = `/design-lab?${params.toString()}`;

    // Track the click event
    const { mixpanelData, mixpanelEvent } = $link.dataset;
    if (!mixpanelEvent) {
      track("start_designing_clicked", {
        ...parseMixpanelData(mixpanelData),
        with_plp_preview: false
      });
    }
  } else {
    // Redirect with JS for button/div elements
    handleRedirect(element, $link);
  }
};

const handleProductCard = (productCard, $link) => {
  const productData = extractProductData(productCard);
  if (productData.catalogNumber) {
    const triggerLocation = productData.isFeatured
      ? TRIGGER_LOCATIONS.TOP_TILE
      : TRIGGER_LOCATIONS.PRODUCT_CARD;
    track("upload_step_viewed", {
      product_id: productData.productId,
      trigger_location: triggerLocation
    });

    collectAndHandleRedirect(productCard, $link);
  }
};

// eslint-disable-next-line max-lines-per-function, max-params
const redirectedDesignLabLink = (e, $link, $element, cookieSnapshot) => {
  // Prefer snapshot captured at click time; fall back to reading from cookie if not provided
  const imageUrl = cookieSnapshot?.imageUrl ?? DesignUploadCtaPlpCookieClass.originalUrl;
  const imageId = cookieSnapshot?.imageId ?? DesignUploadCtaPlpCookieClass.previewId;
  const imageType = cookieSnapshot?.imageType ?? DesignUploadCtaPlpCookieClass.originalFiletype;

  if (!!imageUrl && !!imageId && !!imageType) {
    e.preventDefault();
    const $productCard = $link.closest(".js-product-card-v3");
    let productData = {};
    if ($productCard) {
      productData = extractProductData($productCard);
    } else if ($element) {
      const baseData = extractProductData($element);
      const fallbackData = extractProductDataWithFallback($element, baseData);
      productData = { ...baseData, ...fallbackData };
    }
    if (!productData || !productData.catalogNumber) {
      productData = { ...productData, catalogNumber: "G640", color: "White" };
    }
    const activeColor = productData.color || getActiveColorName();
    const pdpData = activeColor
      ? collectPdpData(activeColor)
      : { quantities: null, sizeData: null };

    const url = buildDesignLabUrl({
      imageUrl,
      imageId,
      imageType,
      productData,
      pdpQuantities: pdpData.quantities,
      pdpSizeData: pdpData.sizeData
    });

    const { mixpanelData } = $link.dataset;
    track("start_designing_clicked", {
      ...parseMixpanelData(mixpanelData),
      with_plp_preview: true
    });

    window.location.href = url;

    return true;
  }

  return false;
};

// eslint-disable-next-line max-lines-per-function
function initGlobalStartDesigning() {
  initDesignUploadModal();

  // eslint-disable-next-line max-lines-per-function
  on("click", START_DESIGNING_SELECTOR, (e, $link) => {
    e.stopPropagation();

    // Snapshot PLP preview cookie values synchronously, then clear cookie immediately.
    // We rely only on button clicks for clearing, per product requirement.
    const cookieSnapshot = {
      imageUrl: DesignUploadCtaPlpCookieClass.originalUrl,
      imageId: DesignUploadCtaPlpCookieClass.previewId,
      imageType: DesignUploadCtaPlpCookieClass.originalFiletype
    };
    if (cookieSnapshot.imageUrl || cookieSnapshot.imageId || cookieSnapshot.imageType) {
      DesignUploadCtaPlpCookieClass.destroy();
    }

    const isStateless = e.target.dataset.stateless === "true";
    const $heroCard = $getHeroCard(e.target);
    const $productCard = $getProductCard(e.target);
    const $customsNav = $getCustomsNav(e.target);
    const $pdpUploadButton = $getPdpUploadButton(e.target);
    const $bannerCta = $getBannerCta(e.target);
    const $bannerPromo = $getBannerPromo(e.target);
    const $modalDataset = $getModalDataset();
    // Only treat explicit product contexts as product-derived:
    // - product card (PLP/tiles)
    // - PDP upload button
    // Do NOT treat modal dataset as a product source for generic CTAs.
    // Exception: for the PDP Experience Switcher "Yes, Start Designing" button,
    // we intentionally use the modal dataset to capture PDP product/color context.
    let $element;
    if ($productCard) {
      $element = $productCard;
    } else if ($pdpUploadButton) {
      $element = $pdpUploadButton;
    } else if (isStateless && $modalDataset) {
      $element = $modalDataset;
    } else if ($bannerCta) {
      $element = $bannerCta;
    }

    if ($bannerPromo) {
      redirectTo("/design-lab");
      return;
    }

    // Handle redirected design lab link using the snapped cookie state
    const hasRedirectedWithPreview =
      redirectedDesignLabLink(e, $link, $element, cookieSnapshot) && !isStateless;
    if (hasRedirectedWithPreview) {
      return;
    }

    // Let nav-design-lab links navigate naturally without adding params
    if ($customsNav) {
      return;
    }

    if (!isMobile() || $link.dataset.forceDesktopRedirect === "true") {
      e.preventDefault();
      handleRedirect($element, $link);
      return;
    }

    if ($pdpUploadButton || $bannerCta) {
      return;
    }

    if ($heroCard) {
      handleHeroCard();
    } else if ($productCard) {
      handleProductCard($productCard, $link);
      return;
    } else {
      if ($link.tagName !== "A") {
        e.preventDefault();
      }

      collectAndHandleRedirect($element, $link);
      return;
    }

    // Track the click event for Mixpanel
    // If mixpanelEvent is not defined, we assume it's a custom event from PLP
    // and we track it with the provided mixpanelData
    const { mixpanelData, mixpanelEvent } = $link.dataset;
    if (!mixpanelEvent) {
      track("start_designing_clicked", {
        ...parseMixpanelData(mixpanelData),
        with_plp_preview: false
      });
    }
  });
}

export default initGlobalStartDesigning;
